import { BoxContainer, PrimaryButton, ReactIcons, TopBarProps } from "@kalyzee/kalyzee-ui";

import React, { useState } from "react";

// Ratio between two consecutive priority ranks. Large enough for the lower rank to absorb
// virtually all of the missing space before the next one starts to shrink.
const SHRINK_PRIORITY_RATIO = 1000;

export interface SectionWorkBoxSection {
  key: string;
  defaultFolded?: boolean;
  foldable?: boolean;
  /**
   * How much this section should be protected from shrinking, relatively to the others
   * (default 0). Every section keeps its content size while there is enough room; when space
   * runs out, the lowest priorities shrink (and scroll) first, the highest ones last.
   */
  priority?: number;
  left?: React.ReactNode;
  header: React.ReactNode;
  content?: React.ReactNode;
}

export interface SectionWorkBoxProps {
  style?: React.CSSProperties;
  /** Default value of `foldable` for the sections that don't define it. */
  foldable?: boolean;
  sections: SectionWorkBoxSection[];
  onClose?: () => void;
}

export const SectionWorkBox = React.memo(({ style, foldable, sections, onClose }: SectionWorkBoxProps) => {
  // Only holds the sections the user actually toggled: `defaultFolded` stays the source of
  // truth until then.
  const [foldedSections, setFoldedSections] = useState<{ [key: string]: boolean }>({});

  // CSS spreads the missing space proportionally to `flex-shrink`, with no notion of ordering.
  // To emulate a real priority, ranks are spread over orders of magnitude: a section only starts
  // to shrink once every lower-priority one has been squeezed (flexbox freezes an item that
  // reached its minimum, then redistributes the remaining deficit to the others).
  const ranks = Array.from(new Set(sections.map((s) => s.priority ?? 0))).sort((a, b) => a - b);
  const getSectionShrink = (section: SectionWorkBoxSection) =>
    Math.min(SHRINK_PRIORITY_RATIO ** (ranks.length - 1 - ranks.indexOf(section.priority ?? 0)), 1e15);

  const isSectionFoldable = (section: SectionWorkBoxSection) => section.foldable ?? foldable ?? false;

  const isSectionFolded = (section: SectionWorkBoxSection) =>
    foldedSections[section.key] ?? section.defaultFolded ?? false;

  const onUpdateSection = (section: SectionWorkBoxSection) => {
    setFoldedSections((prev) => ({ ...prev, [section.key]: !isSectionFolded(section) }));
  };

  const getSectionHeaderProps = (section: SectionWorkBoxSection, index: number): TopBarProps => ({
    left: section.left ? [section.left] : [],
    center: isSectionFoldable(section)
      ? [
          <ReactIcons.ThreeDots
            style={{
              height: "1em",
              transform: isSectionFolded(section) ? "rotate(0deg)" : "rotate(90deg)",
              transition: "transform 0.2s",
            }}
          />,
          section.header,
        ]
      : [section.header],
    right:
      onClose && index === 0
        ? [
            <PrimaryButton onClick={onClose}>
              <ReactIcons.Close style={{ height: "0.8em" }} />
            </PrimaryButton>,
          ]
        : [],
    nativeProps: {
      onClick: isSectionFoldable(section) ? () => onUpdateSection(section) : undefined,
      style: {
        cursor: isSectionFoldable(section) ? "pointer" : undefined,
        whiteSpace: "nowrap",
      },
    },
  });

  const renderSections = () => {
    const nodes: React.ReactNode[] = [];

    const renderSectionContent = (section: SectionWorkBoxSection) => {
      if (!section.content) return null;
      return (
        <div
          key={`section-content-${section.key}`}
          style={{
            display: isSectionFolded(section) ? "none" : "flex",
            // Take the content's natural size, but never exceed the available space.
            flexGrow: 0,
            flexShrink: getSectionShrink(section),
            flexBasis: "auto",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {section.content}
        </div>
      );
    };

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      if (i > 0) {
        nodes.push(<BoxContainer.Bar {...getSectionHeaderProps(section, i)} key={`section-header-${section.key}`} />);
      }
      nodes.push(renderSectionContent(section));
    }

    return nodes;
  };

  return (
    <BoxContainer style={style} topBarProps={sections[0] ? getSectionHeaderProps(sections[0], 0) : {}}>
      {renderSections()}
    </BoxContainer>
  );
});
